import React from 'react';

export const TouchInput = ({ 
  label, 
  error, 
  help, 
  required = false,
  className = '',
  ...props 
}) => {
  const inputStyles = {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px', // Prevents zoom on iOS
    lineHeight: '1.5',
    border: `1px solid ${error ? '#F44336' : '#E0E0E0'}`,
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#212121',
    outline: 'none',
    transition: 'border-color 150ms ease-in-out, box-shadow 150ms ease-in-out',
    // Touch-friendly
    minHeight: '48px',
    touchAction: 'manipulation',
    // Prevent zoom on iOS
    webkitAppearance: 'none',
    appearance: 'none'
  };

  const labelStyles = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#212121',
    marginBottom: '6px'
  };

  const errorStyles = {
    display: 'block',
    fontSize: '12px',
    color: '#F44336',
    marginTop: '4px'
  };

  const helpStyles = {
    display: 'block',
    fontSize: '12px',
    color: '#666666',
    marginTop: '4px'
  };

  return (
    <div className={`touch-input ${className}`}>
      {label && (
        <label style={labelStyles}>
          {label}
          {required && <span style={{ color: '#F44336', marginLeft: '4px' }}>*</span>}
        </label>
      )}
      <input
        style={{
          ...inputStyles,
          ':focus': {
            borderColor: '#2196F3',
            boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.2)'
          }
        }}
        className="touch-input__field"
        {...props}
      />
      {error && <span style={errorStyles}>{error}</span>}
      {help && !error && <span style={helpStyles}>{help}</span>}
      
      <style jsx>{`
        .touch-input__field:focus {
          border-color: #2196F3;
          box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
        }
        
        .touch-input__field:disabled {
          background-color: #f5f5f5;
          color: #999999;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export const TouchTextArea = ({ 
  label, 
  error, 
  help, 
  required = false,
  rows = 4,
  className = '',
  ...props 
}) => {
  const textareaStyles = {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    lineHeight: '1.5',
    border: `1px solid ${error ? '#F44336' : '#E0E0E0'}`,
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#212121',
    outline: 'none',
    transition: 'border-color 150ms ease-in-out, box-shadow 150ms ease-in-out',
    resize: 'vertical',
    minHeight: '120px',
    fontFamily: 'inherit',
    // Touch-friendly
    touchAction: 'manipulation',
    webkitAppearance: 'none',
    appearance: 'none'
  };

  const labelStyles = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#212121',
    marginBottom: '6px'
  };

  const errorStyles = {
    display: 'block',
    fontSize: '12px',
    color: '#F44336',
    marginTop: '4px'
  };

  const helpStyles = {
    display: 'block',
    fontSize: '12px',
    color: '#666666',
    marginTop: '4px'
  };

  return (
    <div className={`touch-textarea ${className}`}>
      {label && (
        <label style={labelStyles}>
          {label}
          {required && <span style={{ color: '#F44336', marginLeft: '4px' }}>*</span>}
        </label>
      )}
      <textarea
        style={textareaStyles}
        rows={rows}
        className="touch-textarea__field"
        {...props}
      />
      {error && <span style={errorStyles}>{error}</span>}
      {help && !error && <span style={helpStyles}>{help}</span>}
      
      <style jsx>{`
        .touch-textarea__field:focus {
          border-color: #2196F3;
          box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
        }
        
        .touch-textarea__field:disabled {
          background-color: #f5f5f5;
          color: #999999;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export const TouchSelect = ({ 
  label, 
  error, 
  help, 
  required = false,
  children,
  className = '',
  ...props 
}) => {
  const selectStyles = {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    lineHeight: '1.5',
    border: `1px solid ${error ? '#F44336' : '#E0E0E0'}`,
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#212121',
    outline: 'none',
    transition: 'border-color 150ms ease-in-out, box-shadow 150ms ease-in-out',
    cursor: 'pointer',
    // Touch-friendly
    minHeight: '48px',
    touchAction: 'manipulation',
    webkitAppearance: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
    backgroundPosition: 'right 12px center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '16px',
    paddingRight: '40px'
  };

  const labelStyles = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#212121',
    marginBottom: '6px'
  };

  const errorStyles = {
    display: 'block',
    fontSize: '12px',
    color: '#F44336',
    marginTop: '4px'
  };

  const helpStyles = {
    display: 'block',
    fontSize: '12px',
    color: '#666666',
    marginTop: '4px'
  };

  return (
    <div className={`touch-select ${className}`}>
      {label && (
        <label style={labelStyles}>
          {label}
          {required && <span style={{ color: '#F44336', marginLeft: '4px' }}>*</span>}
        </label>
      )}
      <select
        style={selectStyles}
        className="touch-select__field"
        {...props}
      >
        {children}
      </select>
      {error && <span style={errorStyles}>{error}</span>}
      {help && !error && <span style={helpStyles}>{help}</span>}
      
      <style jsx>{`
        .touch-select__field:focus {
          border-color: #2196F3;
          box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
        }
        
        .touch-select__field:disabled {
          background-color: #f5f5f5;
          color: #999999;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export const TouchCheckbox = ({ 
  label, 
  error, 
  help, 
  className = '',
  ...props 
}) => {
  const checkboxStyles = {
    width: '20px',
    height: '20px',
    margin: '0 12px 0 0',
    cursor: 'pointer',
    accentColor: '#2196F3'
  };

  const labelStyles = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#212121',
    cursor: 'pointer',
    minHeight: '44px', // Touch-friendly
    padding: '12px 0'
  };

  const errorStyles = {
    display: 'block',
    fontSize: '12px',
    color: '#F44336',
    marginTop: '4px'
  };

  const helpStyles = {
    display: 'block',
    fontSize: '12px',
    color: '#666666',
    marginTop: '4px'
  };

  return (
    <div className={`touch-checkbox ${className}`}>
      <label style={labelStyles}>
        <input
          type="checkbox"
          style={checkboxStyles}
          {...props}
        />
        {label}
      </label>
      {error && <span style={errorStyles}>{error}</span>}
      {help && !error && <span style={helpStyles}>{help}</span>}
    </div>
  );
};

export const TouchRadio = ({ 
  label, 
  error, 
  help, 
  className = '',
  ...props 
}) => {
  const radioStyles = {
    width: '20px',
    height: '20px',
    margin: '0 12px 0 0',
    cursor: 'pointer',
    accentColor: '#2196F3'
  };

  const labelStyles = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#212121',
    cursor: 'pointer',
    minHeight: '44px', // Touch-friendly
    padding: '12px 0'
  };

  const errorStyles = {
    display: 'block',
    fontSize: '12px',
    color: '#F44336',
    marginTop: '4px'
  };

  const helpStyles = {
    display: 'block',
    fontSize: '12px',
    color: '#666666',
    marginTop: '4px'
  };

  return (
    <div className={`touch-radio ${className}`}>
      <label style={labelStyles}>
        <input
          type="radio"
          style={radioStyles}
          {...props}
        />
        {label}
      </label>
      {error && <span style={errorStyles}>{error}</span>}
      {help && !error && <span style={helpStyles}>{help}</span>}
    </div>
  );
};

export const TouchFormGroup = ({ 
  children, 
  className = '',
  ...props 
}) => {
  const groupStyles = {
    marginBottom: '24px'
  };

  return (
    <div 
      style={groupStyles}
      className={`touch-form-group ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

export const TouchForm = ({ 
  children, 
  className = '',
  ...props 
}) => {
  const formStyles = {
    width: '100%'
  };

  return (
    <form 
      style={formStyles}
      className={`touch-form ${className}`}
      {...props}
    >
      {children}
    </form>
  );
};